import { useEffect, useMemo } from 'react';

import { useScopedTranslate } from '@locale/translate';

import { useRotatingLoadingMessagesContext } from './rotating-loading-messages-provider';

export const useRotatingLoadingMessages = ({
  isEnabled,
  title,
  messages,
  firstMessage = undefined,
  minimumDelay = 1000,
}: {
  isEnabled: boolean;
  title?: string;
  messages?: string[];
  firstMessage?: string;
  minimumDelay?: number;
}) => {
  const { show, hide, delayedState } = useRotatingLoadingMessagesContext();
  const { t } = useScopedTranslate(locale);

  const defaultMessages = useMemo(
    () =>
      [
        firstMessage,
        t('doingMagic'),
        t('almostLive'),
        t('organizingChaos'),
        t('verifyingJourney'),
        t('validating'),
        t('checkingIfOverlooked'),
        t('perfectingTheWorkflow'),
        t('workflowNearlyThere'),
      ].filter(Boolean) as string[],
    [firstMessage, t],
  );

  useEffect(() => {
    return () => {
      hide();
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isEnabled) {
      show({ title: title ?? t('hangOn'), messages: messages ?? defaultMessages, minimumDelay });
    } else {
      hide();
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [isEnabled]);

  return delayedState;
};

const locale = {
  en: {
    hangOn: 'Hang on',
    doingMagic: 'Workflow magic in progress...',
    almostLive: 'Almost live, preparing for takeoff...',
    organizingChaos: 'Organizing chaos into order...',
    verifyingJourney: 'Verifying journey integrity...',
    validating: 'Validating all steps...',
    checkingIfOverlooked: 'Making sure nothing is overlooked...',
    perfectingTheWorkflow: 'Perfecting your workflow masterpiece...',
    workflowNearlyThere: 'Workflow nearly there, just a moment more...',
  },
};
